import { createContext, type ReactNode, useCallback, useContext, useState } from 'react'

import { Notification } from '@hrworks/sui-shared/classes'
import type { AddNotificationProps } from '@hrworks/sui-shared/classes/Notification/Notification.types'

interface NotificationContextValue {
  notifications: Notification[]
  addNotification: (props: Omit<AddNotificationProps, 'type'>) => void
}

const NotificationContext = createContext<NotificationContextValue | null>(null)

export const NotificationProvider = ({ children }: { children: ReactNode }) => {
  const [notifications, setNotifications] = useState<Notification[]>([])

  const removeNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id))
  }, [])

  const addNotification = useCallback(
    (props: Omit<AddNotificationProps, 'type'>) => {
      const notification = new Notification({ type: 'addNotification', ...props }, removeNotification)
      setNotifications((prev) => [...prev, notification])
    },
    [removeNotification],
  )

  return (
    <NotificationContext.Provider value={{ notifications, addNotification }}>
      {children}
    </NotificationContext.Provider>
  )
}

export const useNotifications = (): NotificationContextValue => {
  const ctx = useContext(NotificationContext)
  if (!ctx) {
    throw new Error('useNotifications must be used within NotificationProvider')
  }
  return ctx
}
